import {
  calculateTotalHours,
  formatDateForAPI,
  formatDateForDisplay,
  getDayFromDate,
} from "utils/utils";
import { differenceInDays, subDays } from "date-fns";
import { TIMESHEET_QUERY_KEYS } from "components/timesheet/query-keys";
import { toast } from "sonner";
import { queryClient } from "utils/react-query";
import { deleteTimesheetEntry } from "./api";
import { invalidateTimesheetQueries } from "components/timesheet/queries";

// export const calculateTotalHours = (entries, field) => {
//   return entries.reduce((sum, entry) => sum + (Number(entry[field]) || 0), 0);
// };

export const calculateTotalHoursWithConsumptionFactor = (entries, field) => {
  return entries.reduce(
    (sum, entry) =>
      sum +
      (Number(entry[field]) || 0) *
      ((entry.consumption_factor || entry.employee.consumption_factor) / 100),
    0
  );
};

export function formatHoursToTime(hours) {
  // Convert input to number if it's a string
  const numericHours = typeof hours === "string" ? parseFloat(hours) : hours;

  // Handle invalid input
  if (isNaN(numericHours)) {
    return null;
  }

  const isNegative = numericHours < 0;
  const absHours = Math.abs(numericHours);

  // Extract whole hours and calculate minutes
  const wholeHours = Math.floor(absHours);
  const minutes = Math.round((absHours - wholeHours) * 60);

  // Handle case where minutes calculation results in 60 (should roll over to hour)
  const adjustedHours = minutes === 60 ? wholeHours + 1 : wholeHours;
  const adjustedMinutes = minutes === 60 ? 0 : minutes;

  // Format the output based on values
  if (adjustedHours === 0 && adjustedMinutes === 0) {
    return "0h";
  }

  const prefix = isNegative ? "-" : "";

  if (adjustedHours === 0) {
    return `${prefix}${adjustedMinutes}m`;
  } else if (adjustedMinutes === 0) {
    return `${prefix}${adjustedHours}h`;
  } else {
    return `${prefix}${adjustedHours}h ${adjustedMinutes}m`;
  }
}

/**
 * Transforms raw daily timesheet data into a structure optimized for UI display
 * @param {Array} rawDailyTimesheetData - Raw data for a single day from the API
 * @returns {Object} Structured data ready for UI consumption for a single day
 */
export function transformDailyTimesheetLog(
  rawDailyTimesheetData,
  capacityData,
  date
) {
  // Assuming the API returns an array of entries for the day
  const entries = rawDailyTimesheetData;

  const totalLogsInADay = calculateTotalHours(entries, "logged_hours");
  const totalApprovedInADay = calculateTotalHours(entries, "approved_hours");
  const totalUnApprovedInADay = calculateTotalHours(
    entries,
    "unapproved_hours"
  );

  // Extract capacity info from capacityData (assuming it's an array with one item for the day)
  const capacityInfo = capacityData?.[0] || {
    payroll_hours: 0,
    is_logged: false,
    id: undefined,
  };
  const payrollHours = capacityInfo.payroll_hours || 0;
  const isAcknowledged = capacityInfo.is_logged; // Use capacity is_logged if available

  return {
    date: date,
    formattedDate: formatDateForDisplay(date),
    day: getDayFromDate(date),
    entries,
    totalLogsInADay,
    totalApprovedInADay,
    totalUnApprovedInADay,
    payrollHours,
    isAcknowledged,
    dailyLoggedPercentage:
      payrollHours === 0
        ? 0
        : Math.min(
          100,
          Number(((totalLogsInADay / payrollHours) * 100).toFixed(2)) || 0
        ),
    dailyApprovedPercentage:
      payrollHours === 0
        ? 0
        : Math.min(
          100,
          Number(((totalApprovedInADay / payrollHours) * 100).toFixed(2)) || 0
        ),
    isComplete: totalLogsInADay >= payrollHours,
  };
}

/**
 * Transforms raw draft timesheet data into a structure optimized for UI display
 * @param {Array} rawDraftTimesheetData - Raw draft data from the API
 * @param {string} date - The date for the timesheet
 * @returns {Object} Structured draft data ready for UI consumption
 */
export function transformDraftTimesheetLog(rawDraftTimesheetData, date) {
  // Assuming the API returns an array of entries for the day
  const entries = rawDraftTimesheetData;

  const totalLogsInADay = calculateTotalHours(entries, "logged_hours");

  return {
    date: date,
    formattedDate: formatDateForDisplay(date),
    day: getDayFromDate(date),
    entries,
    totalLogsInADay,
  };
}

/**
 * Transforms raw timesheet data into a structure optimized for UI display
 * @param {Array} rawTimesheetData - Raw data from the API
 * @param {Array} capacityData - Daily work capacity data
 * @returns {Object} Structured data ready for UI consumption
 */
export function transformDailyTimesheetReview(rawDailyTimesheetData, date) {
  const entries = rawDailyTimesheetData;

  const totalLogsInADay = calculateTotalHours(entries, "logged_hours");
  const totalApprovedInADay = calculateTotalHours(entries, "approved_hours");
  const totalUnApprovedInADay = calculateTotalHours(
    entries,
    "unapproved_hours"
  );

  // Extract capacity info from capacityData (assuming it's an array with one item for the day)

  return {
    date: date,
    formattedDate: formatDateForDisplay(date),
    day: getDayFromDate(date),
    entries,
    totalLogsInADay,
    totalApprovedInADay,
    totalUnApprovedInADay,
    areLogsReviewed:
      totalLogsInADay > 0 &&
      totalApprovedInADay + totalUnApprovedInADay === totalLogsInADay,
  };
}


/**
 * Calculates the day offset between a target date and today
 * @param {string | Date} targetDate - The date to calculate offset for
 * @returns {number} - Number of days between target date and today
 */
export const encodeDateToOffset = (targetDate) => {
  const today = new Date();
  const target = new Date(targetDate);
  return differenceInDays(today, target);
};

/**
 * Converts a day offset to an actual date
 * @param {number} dayOffset - Number of days from today
 * @returns {Date} - The calculated date
 */
export const decodeDateFromOffset = (dayOffset) => {
  const today = new Date();
  return formatDateForAPI(subDays(today, dayOffset));
};

export async function handleDeleteTimesheetEntry({ entryId, date }) {
  try {
    // Define query keys
    const timesheetQueryKey = [TIMESHEET_QUERY_KEYS.TIMESHEET_LOG, date];
    const reviewQueryKey = [TIMESHEET_QUERY_KEYS.TIMESHEET_REVIEW, date];

    // Get current data for rollback
    const previousTimesheet = queryClient.getQueryData(timesheetQueryKey);
    const previousTimesheetReview = queryClient.getQueryData(reviewQueryKey);

    // Check if data exists
    if (!previousTimesheet) {
      console.warn("⚠️ No timesheet data found in cache");
      toast.error("No data found to delete");
      return;
    }

    // Optimistic update for timesheet
    queryClient.setQueryData(timesheetQueryKey, (old) => {
      if (!old || !Array.isArray(old)) {
        console.warn("⚠️ Timesheet data is not an array:", old);
        return old;
      }

      const newData = old.filter((entry) => {
        const shouldKeep = entry.id !== entryId;
        return shouldKeep;
      });

      return newData;
    });

    // Optimistic update for review
    queryClient.setQueryData(reviewQueryKey, (old) => {
      if (!old || !Array.isArray(old)) {
        console.warn("⚠️ Review data is not an array:", old);
        return old;
      }

      const newData = old.filter((entry) => entry.id !== entryId);
      return newData;
    });

    try {
      // Make the API call
      await deleteTimesheetEntry({ id: entryId });

      toast.success("Entry Deleted Successfully");

      invalidateTimesheetQueries(date);
    } catch (error) {
      console.error("❌ Delete API call failed:", error);

      // Rollback optimistic updates
      queryClient.setQueryData(timesheetQueryKey, previousTimesheet);
      queryClient.setQueryData(reviewQueryKey, previousTimesheetReview);

      // Invalidate to ensure UI updates
      queryClient.invalidateQueries({ queryKey: timesheetQueryKey });
      queryClient.invalidateQueries({ queryKey: reviewQueryKey });

      throw error;
    }
  } catch (error) {
    console.error("💥 Error in handleDeleteTimesheetEntry:", error);
    toast.error("Failed to delete the entry");
  }
}
